import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

export interface CartItem {
  cartItemId: string;
  productId: string;
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  discount?: number;  
  image: string;
  quantity: number;
  category: string;
  isOverseas?: boolean;
  selectedColor?: string;
  selectedSize?: string;
  selectedSizeCm?: number;
  sizeStockUsed?: number;
  variationId?: string; 
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: string) => void;
  removeSelectedItems: (ids: string[]) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      const savedCart = localStorage.getItem(`cart_${user.id}`);
      if (savedCart) setCart(JSON.parse(savedCart));
    }
  }, [user]);

  useEffect(() => {
    if (user) localStorage.setItem(`cart_${user.id}`, JSON.stringify(cart));
  }, [cart, user]);

  const addToCart = (item: CartItem) => {
    setCart(prev => {
      // Use cartItemId for unique identification, not _id
      const uniqueKey = `${item.cartItemId || item.id}-${item.selectedColor || ''}-${item.selectedSize || ''}`;
      
      const existing = prev.find(i => {
        const existingKey = `${i.cartItemId || i.id}-${i.selectedColor || ''}-${i.selectedSize || ''}`;
        return existingKey === uniqueKey;
      });
      
      if (existing) {
        // Update quantity if the exact same variant exists
        return prev.map(i => {
          const iKey = `${i.cartItemId || i.id}-${i.selectedColor || ''}-${i.selectedSize || ''}`;
          return iKey === uniqueKey 
            ? { ...i, quantity: i.quantity + item.quantity }
            : i;
        });
      }
      return [...prev, item];
    });
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const removeSelectedItems = (ids: string[]) => {
    setCart(prev => prev.filter(item => !ids.includes(item.id)));
  };

  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(id);
      return;
    }
    setCart(prev => prev.map(item => item.id === id ? { ...item, quantity } : item));
  };

  const clearCart = () => setCart([]);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  return (
    <CartContext.Provider value={{ 
      cart, 
      addToCart, 
      removeFromCart, 
      removeSelectedItems,
      updateQuantity, 
      clearCart, 
      totalItems, 
      totalPrice 
    }}>
      {children}
    </CartContext.Provider>
  );
};